/**
 * The tower simulation: auras, aim, cooldowns, and the translation from an
 * upgraded stat block into an actual shot.
 *
 * Runs in a fixed order every step, and the order is load-bearing:
 *
 * 1. **Index** — rebuild the enemy spatial hash. Everything downstream queries it.
 * 2. **Auras** — Pylons write damage/rate/cash multipliers into per-tower
 *    scratch arrays; Cryo permafrost rings chill enemies directly. Auras are
 *    recomputed from scratch rather than accumulated, so selling a Pylon takes
 *    effect on the very next step with no bookkeeping.
 * 3. **Towers** — cool down, acquire, aim, fire.
 *
 * Aim smoothing exists for one reason: a barrel that snaps instantly to a new
 * target reads as a bug at 640×360, and a barrel that lags too far reads as
 * broken. `TowerBehaviour.turnSeconds` is the dial, and slow-turning towers
 * (Mortar, Sniper) hold fire until the barrel is inside `ALIGN_TOLERANCE` so
 * you can see them track before they commit.
 *
 * ## Allocation
 *
 * Scratch arrays grow when the roster grows and never shrink. Every callback
 * handed to the enemy index is pre-bound in the constructor.
 */

import { VIEW } from '@/core/config';
import type {
  Enemy, System, TargetingMode, Tower, TowerArchetype, World,
} from '@/core/types';
import { behaviourOf, type TowerBehaviour } from './Catalog';
import { applyUpgrades, collectGrants, grantArg, grantNumber, hasGrant, totalInvested } from './Upgrades';
import { EnemyIndex, NO_TARGET } from './Targeting';
import { ProjectileSystem } from './Projectiles';

/** Radians of aim error a slow-turning tower tolerates before it will fire. */
const ALIGN_TOLERANCE = 0.28;
/** Widest range the `global` grant resolves to. */
const GLOBAL_RANGE = Math.hypot(VIEW.width, VIEW.height);
/** Fallback chain reach when a node did not specify one. */
const CHAIN_REACH = 52;

/**
 * Every grant value a tower needs at runtime, resolved once per tier change.
 *
 * Parsing `grants` on demand was the original design and it allocated: a
 * `for...of` over a `Set` builds an iterator, `copyRiders` did that eleven
 * times per shot, and a Hurricane Drum fires nineteen times a second. Caching
 * against a packed tier signature turns all of it into array reads.
 */
interface Resolved {
  /** Packed tiers plus tower id, so a sold-and-rebuilt slot invalidates. */
  sig: number;
  ownerId: number;

  auraDamage: number; auraRate: number; auraCash: number; auraSlow: number;
  chain: number; chainFalloff: number;
  cone: number; prox: number;
  cluster: number; clusterFraction: number;
  napalmDps: number; napalmSecs: number; napalmRadius: number;
  slowFactor: number; slowSecs: number;
  stunSecs: number; stunChance: number;
  shatter: number; shieldMelt: number;
  dotDps: number; dotSecs: number; dotStacks: number;
  spreadRadius: number; spreadFraction: number;
  pull: number; detonateFraction: number; detonateRadius: number;
  rampPerSec: number; rampMax: number; split: number;
  global: boolean;
}

function blankResolved(): Resolved {
  return {
    sig: -1, ownerId: -1,
    auraDamage: 0, auraRate: 0, auraCash: 0, auraSlow: 1,
    chain: 0, chainFalloff: 0, cone: 0, prox: 0,
    cluster: 0, clusterFraction: 0.5,
    napalmDps: 0, napalmSecs: 0, napalmRadius: 0,
    slowFactor: 1, slowSecs: 0, stunSecs: 0, stunChance: 0,
    shatter: 1, shieldMelt: 1,
    dotDps: 0, dotSecs: 0, dotStacks: 1,
    spreadRadius: 0, spreadFraction: 0,
    pull: 0, detonateFraction: 0, detonateRadius: 0,
    rampPerSec: 0, rampMax: 0, split: 1,
    global: false,
  };
}

let nextTowerId = 1;

/** Builds a placed tower at full base stats. `main`/`ui` own placement rules. */
export function createTower(archetype: TowerArchetype, x: number, y: number): Tower {
  const tiers: [number, number, number] = [0, 0, 0];
  return {
    id: nextTowerId++,
    archetype,
    pos: { x, y },
    tiers,
    stats: applyUpgrades(archetype, tiers),
    targeting: 'first',
    cooldown: 0,
    angle: -Math.PI / 2,
    damageDealt: 0,
    kills: 0,
    invested: totalInvested(archetype, tiers),
    grants: new Set<string>(),
    flash: 0,
    selected: false,
  };
}

/** Resets the id counter. Only the deterministic test harness should call this. */
export function resetTowerIds(): void { nextTowerId = 1; }

export class TowerSystem implements System {
  readonly name = 'towers';

  /** Per-tower aura accumulators, indexed by position in `world.towers`. */
  private auraDamage = new Float32Array(64);
  private auraRate = new Float32Array(64);
  private auraCash = new Float32Array(64);
  /** Laser ramp state: accumulated bonus and the enemy id it was earned against. */
  private ramp = new Float32Array(64);
  private rampTarget = new Int32Array(64);
  /** Remaining cooldown per granted ability, flattened as `towerIndex*ABILITY_SLOTS`. */
  private abilityCd = new Float32Array(64 * 4);

  /** Resolved grant values, one per tower slot, keyed by tier signature. */
  private resolved: Resolved[] = [];

  /** Scratch for split beams. */
  private splitIdx = new Int32Array(32);
  private splitCount = 0;
  private splitWant = 0;

  // Sweep state for the Cryo permafrost ring.
  private auraWorld: World | null = null;
  private auraSlowFactor = 1;

  private readonly onChill: (e: Enemy, i: number, d2: number) => void;
  private readonly onSplit: (e: Enemy, i: number, d2: number) => void;

  constructor(
    private readonly index: EnemyIndex,
    private readonly projectiles: ProjectileSystem,
  ) {
    this.onChill = (e) => this.chillOne(e);
    this.onSplit = (_e, i) => {
      if (this.splitCount < this.splitWant && this.splitCount < this.splitIdx.length) {
        this.splitIdx[this.splitCount++] = i;
      }
    };
  }

  // ─────────────────────────────────────────────────────────────────── step ──

  /**
   * Refreshes the resolved-grant cache for one tower slot. Cheap when nothing
   * changed, which is every step except the one right after a purchase.
   */
  private resolveFor(i: number, t: Tower): Resolved {
    let r = this.resolved[i];
    if (r === undefined) { r = blankResolved(); this.resolved[i] = r; }
    const sig = t.tiers[0] | (t.tiers[1] << 3) | (t.tiers[2] << 6);
    if (r.sig === sig && r.ownerId === t.id) return r;

    r.sig = sig; r.ownerId = t.id;
    const g = t.grants;
    const b = behaviourOf(t.archetype.id);

    r.auraDamage = grantNumber(g, 'aura:damage', 0);
    r.auraRate = grantNumber(g, 'aura:rate', 0);
    r.auraCash = grantNumber(g, 'aura:cash', 0);
    r.auraSlow = grantNumber(g, 'aura:slow', 1, true);
    r.chain = grantNumber(g, 'chain', b.chain);
    r.chainFalloff = grantNumber(g, 'chainFalloff', b.chainFalloff);
    r.cone = grantNumber(g, 'cone', b.cone);
    r.prox = grantNumber(g, 'prox', 0);
    r.cluster = grantNumber(g, 'cluster', 0);
    r.clusterFraction = grantArg(g, 'cluster', 1, 0.5);
    r.napalmDps = grantArg(g, 'napalm', 0, 0);
    r.napalmSecs = grantArg(g, 'napalm', 1, 0);
    r.napalmRadius = grantArg(g, 'napalm', 2, 0);
    r.slowFactor = grantNumber(g, 'slow', 1, true);
    r.slowSecs = r.slowFactor < 1 ? grantArg(g, 'slow', 1, 1) : 0;
    r.stunSecs = grantNumber(g, 'stun', 0);
    r.stunChance = grantNumber(g, 'stunChance', 0);
    r.shatter = grantNumber(g, 'shatter', 1);
    r.shieldMelt = grantNumber(g, 'shieldMelt', 1);
    r.dotDps = grantArg(g, 'dot', 0, 0);
    r.dotSecs = grantArg(g, 'dot', 1, 0);
    r.dotStacks = grantNumber(g, 'stacks', 1);
    r.spreadRadius = grantArg(g, 'spreadOnDeath', 0, 0);
    r.spreadFraction = grantArg(g, 'spreadOnDeath', 1, 0);
    r.pull = grantNumber(g, 'pull', 0);
    r.detonateFraction = grantArg(g, 'detonate', 0, 0);
    r.detonateRadius = grantArg(g, 'detonate', 1, 0);
    r.rampPerSec = grantArg(g, 'ramp', 0, 0);
    r.rampMax = grantArg(g, 'ramp', 1, 0);
    r.split = Math.max(1, grantNumber(g, 'split', 1));
    r.global = hasGrant(g, 'global');
    return r;
  }

  step(dt: number, world: World): void {
    const towers = world.towers;
    this.ensureCapacity(towers.length);
    for (let i = 0; i < towers.length; i++) this.resolveFor(i, towers[i]);
    this.index.rebuild(world.enemies);
    this.applyAuras(dt, world);

    for (let i = 0; i < towers.length; i++) {
      const t = towers[i];
      const b = behaviourOf(t.archetype.id);

      if (t.flash > 0) {
        t.flash -= dt * 8;
        if (t.flash < 0) t.flash = 0;
      }
      this.tickAbilities(i, dt);

      if (b.kind === 'none' || t.stats.fireRate <= 0) continue;

      const rate = t.stats.fireRate * (1 + this.auraRate[i]);
      if (t.cooldown > 0) t.cooldown -= dt;

      const range = this.resolved[i].global ? GLOBAL_RANGE : t.stats.range;
      const targetIdx = this.index.selectTarget(t, t.targeting, range);

      if (targetIdx === NO_TARGET) {
        // Nothing to shoot: let the ramp bleed off and leave the barrel put.
        this.ramp[i] *= 1 - Math.min(1, dt * 1.5);
        this.rampTarget[i] = -1;
        continue;
      }

      const target = this.index.list[targetIdx];
      const wantAngle = Math.atan2(target.pos.y - t.pos.y, target.pos.x - t.pos.x);
      const err = this.steer(t, wantAngle, b, dt);

      if (t.cooldown > 0) continue;
      if (b.turnSeconds > 0.25 && Math.abs(err) > ALIGN_TOLERANCE) continue;

      t.cooldown += 1 / Math.max(0.01, rate);
      // A tower that has been idle must not bank cooldown and then dump shots.
      if (t.cooldown < 0) t.cooldown = 1 / Math.max(0.01, rate);

      this.fire(world, t, i, b, target, dt);
      t.flash = 1;
      this.muzzleFx(world, t, b);
    }
  }

  /** Rotates the barrel toward `want` and returns the remaining error. */
  private steer(t: Tower, want: number, b: TowerBehaviour, dt: number): number {
    let d = want - t.angle;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    const maxStep = b.turnSeconds > 0 ? (Math.PI * 2 / b.turnSeconds) * dt : Math.abs(d);
    const step = Math.abs(d) < maxStep ? d : Math.sign(d) * maxStep;
    t.angle += step;
    if (t.angle > Math.PI) t.angle -= Math.PI * 2;
    else if (t.angle < -Math.PI) t.angle += Math.PI * 2;
    return d - step;
  }

  // ────────────────────────────────────────────────────────────────── auras ──

  private applyAuras(dt: number, world: World): void {
    const towers = world.towers;
    const n = towers.length;
    this.auraDamage.fill(0, 0, n);
    this.auraRate.fill(0, 0, n);
    this.auraCash.fill(0, 0, n);

    for (let s = 0; s < n; s++) {
      const src = towers[s];
      if (src.grants.size === 0) continue;

      const r = this.resolved[s];
      const dmg = r.auraDamage, rate = r.auraRate, cash = r.auraCash, chill = r.auraSlow;
      if (dmg === 0 && rate === 0 && cash === 0 && chill >= 1) continue;

      const r2 = src.stats.range * src.stats.range;

      if (dmg !== 0 || rate !== 0 || cash !== 0) {
        for (let d = 0; d < n; d++) {
          if (d === s) continue;
          const dx = towers[d].pos.x - src.pos.x;
          const dy = towers[d].pos.y - src.pos.y;
          if (dx * dx + dy * dy > r2) continue;
          // Multiple Pylons stack additively. Two 90% coils give 180%, not 261%.
          this.auraDamage[d] += dmg;
          this.auraRate[d] += rate;
          this.auraCash[d] += cash;
        }
      }

      if (chill < 1) {
        this.auraWorld = world;
        this.auraSlowFactor = chill;
        // Refreshed a little longer than a step so the effect never flickers.
        this.index.forEachInRadius(src.pos.x, src.pos.y, src.stats.range, this.onChill);
        this.auraWorld = null;
        if ((world.tick & 7) === 0) {
          const a = world.rng() * Math.PI * 2;
          const rr = src.stats.range * Math.sqrt(world.rng());
          this.projectiles.fx.particle(world,
            src.pos.x + Math.cos(a) * rr, src.pos.y + Math.sin(a) * rr,
            0, -8, 0.7, 1, 'cyan', 'smoke', 1.2, 0.6);
        }
      }
    }

    // Publish the cash multipliers the damage resolver reads.
    const bonus = this.projectiles.cashBonus;
    bonus.clear();
    for (let i = 0; i < n; i++) {
      if (this.auraCash[i] !== 0) bonus.set(towers[i].id, 1 + this.auraCash[i]);
    }
    void dt;
  }

  private chillOne(e: Enemy): void {
    const world = this.auraWorld;
    if (!world) return;
    this.projectiles.applySlow(world, e, this.auraSlowFactor, 0.25);
  }

  // ─────────────────────────────────────────────────────────────────── fire ──

  private fire(world: World, t: Tower, i: number, b: TowerBehaviour, target: Enemy, dt: number): void {
    const r = this.resolved[i];
    const damage = t.stats.damage * (1 + this.auraDamage[i]);
    const mx = t.pos.x + Math.cos(t.angle) * b.muzzle;
    const my = t.pos.y + Math.sin(t.angle) * b.muzzle;

    switch (b.kind) {
      case 'beam': this.fireBeam(world, t, i, b, r, target, damage, dt); return;
      case 'cone': this.fireCone(world, t, b, r, target, damage, mx, my); return;
      case 'ground': this.fireGround(world, t, b, r, target, damage, mx, my); return;
      case 'well': this.fireWell(world, t, b, r, target, damage, mx, my); return;
      case 'chain': this.fireChain(world, t, b, r, target, damage, mx, my); return;
      default: this.fireBolt(world, t, b, r, target, damage, mx, my); return;
    }
  }

  /** Straight or homing single projectile. Turret, Acid, Sniper. */
  private fireBolt(world: World, t: Tower, b: TowerBehaviour, r: Resolved, target: Enemy, damage: number, mx: number, my: number): void {
    const s = this.projectiles.begin();
    const speed = Math.max(40, t.stats.projectileSpeed);
    this.aimLead(s, mx, my, target, speed);
    s.kind = 'bolt';
    s.x = mx; s.y = my;
    s.speed = speed;
    s.ttl = Math.min(4, t.stats.range / speed + 0.6);
    s.damage = damage;
    s.damageType = t.stats.damageType;
    s.pierce = Math.max(1, t.stats.pierce);
    s.splash = t.stats.splash;
    s.armourPierce = t.stats.armourPierce;
    s.critChance = t.stats.critChance;
    s.critMultiplier = t.stats.critMultiplier;
    s.colour = t.archetype.colour;
    s.homing = b.homing;
    s.targetId = target.id;
    s.ownerId = t.id;
    s.trailed = b.trailed;
    this.copyRiders(r, s);
    this.projectiles.launch();
  }

  /** A fan of pellets, one per point of pierce. Flak, Cryo. */
  private fireCone(world: World, t: Tower, b: TowerBehaviour, r: Resolved, target: Enemy, damage: number, mx: number, my: number): void {
    const half = r.cone;
    const pellets = Math.max(1, t.stats.pierce);
    const speed = Math.max(60, t.stats.projectileSpeed);
    const base = Math.atan2(target.pos.y - my, target.pos.x - mx);

    for (let k = 0; k < pellets; k++) {
      // Even spread, jittered, so the pattern never reads as a fixed comb.
      const f = pellets === 1 ? 0 : (k / (pellets - 1)) * 2 - 1;
      const a = base + f * half + (world.rng() - 0.5) * half * 0.25;
      const s = this.projectiles.begin();
      s.kind = 'pellet';
      s.x = mx; s.y = my;
      s.dirX = Math.cos(a); s.dirY = Math.sin(a);
      s.speed = speed * (0.9 + world.rng() * 0.2);
      s.ttl = t.stats.range / speed;
      s.damage = damage;
      s.damageType = t.stats.damageType;
      s.pierce = 1;
      s.splash = t.stats.splash;
      s.splashFalloff = 0.45;
      s.armourPierce = t.stats.armourPierce;
      s.critChance = t.stats.critChance;
      s.critMultiplier = t.stats.critMultiplier;
      s.colour = t.archetype.colour;
      s.ownerId = t.id;
      s.proxRadius = r.prox;
      this.copyRiders(r, s);
      this.projectiles.launch();
    }
  }

  /** Lobbed shell at a predicted ground point. Mortar. */
  private fireGround(world: World, t: Tower, b: TowerBehaviour, r: Resolved, target: Enemy, damage: number, mx: number, my: number): void {
    const speed = Math.max(60, t.stats.projectileSpeed);
    const dist = Math.hypot(target.pos.x - mx, target.pos.y - my);
    const flight = Math.max(0.15, dist / speed);
    // Lead the shot by the full flight time — a mortar that lands behind the
    // target every time is the single most infuriating tower in the genre.
    const tx = target.pos.x + target.vel.x * flight;
    const ty = target.pos.y + target.vel.y * flight;

    const s = this.projectiles.begin();
    s.kind = 'arc';
    s.x = mx; s.y = my;
    s.tx = tx; s.ty = ty;
    s.flight = flight;
    s.dirX = (tx - mx) / flight; s.dirY = (ty - my) / flight;
    s.speed = 1;
    s.ttl = flight + 0.1;
    s.damage = damage;
    s.damageType = t.stats.damageType;
    s.pierce = 1;
    s.splash = Math.max(6, t.stats.splash);
    s.splashFalloff = 0.4;
    s.armourPierce = t.stats.armourPierce;
    s.critChance = t.stats.critChance;
    s.critMultiplier = t.stats.critMultiplier;
    s.colour = t.archetype.colour;
    s.ownerId = t.id;
    s.trailed = b.trailed;
    s.clusterCount = r.cluster;
    s.clusterFraction = r.clusterFraction;
    s.napalmDps = r.napalmDps;
    s.napalmSecs = r.napalmSecs;
    s.napalmRadius = r.napalmRadius;
    this.copyRiders(r, s);
    this.projectiles.launch();
    void world;
  }

  /** Gravity charge lobbed onto the track, arming into a well. Singularity. */
  private fireWell(world: World, t: Tower, b: TowerBehaviour, r: Resolved, target: Enemy, damage: number, mx: number, my: number): void {
    const speed = Math.max(60, t.stats.projectileSpeed);
    const dist = Math.hypot(target.pos.x - mx, target.pos.y - my);
    const flight = Math.max(0.15, dist / speed);

    const s = this.projectiles.begin();
    s.kind = 'arc';
    s.x = mx; s.y = my;
    s.tx = target.pos.x + target.vel.x * flight * 0.5;
    s.ty = target.pos.y + target.vel.y * flight * 0.5;
    s.flight = flight;
    s.dirX = (s.tx - mx) / flight; s.dirY = (s.ty - my) / flight;
    s.speed = 1;
    s.ttl = flight + 0.1;
    // The arrival hit is a fraction; the well is where the damage really is.
    s.damage = damage;
    s.damageType = t.stats.damageType;
    s.pierce = 1;
    s.splash = Math.max(10, t.stats.splash);
    s.splashFalloff = 0.6;
    s.armourPierce = t.stats.armourPierce;
    s.colour = t.archetype.colour;
    s.ownerId = t.id;
    s.trailed = b.trailed;
    s.pullForce = r.pull;
    s.wellLife = 1.6;
    s.detonateFraction = r.detonateFraction;
    s.detonateRadius = r.detonateRadius;
    this.copyRiders(r, s);
    this.projectiles.launch();
    void b; void world;
  }

  /** Fast bolt that jumps between targets on contact. Tesla. */
  private fireChain(world: World, t: Tower, b: TowerBehaviour, r: Resolved, target: Enemy, damage: number, mx: number, my: number): void {
    const s = this.projectiles.begin();
    const speed = Math.max(200, t.stats.projectileSpeed);
    this.aimLead(s, mx, my, target, speed);
    s.kind = 'chain';
    s.x = mx; s.y = my;
    s.speed = speed;
    s.ttl = Math.min(2, t.stats.range / speed + 0.3);
    s.damage = damage;
    s.damageType = t.stats.damageType;
    s.pierce = 1;
    s.splash = t.stats.splash;
    s.armourPierce = t.stats.armourPierce;
    s.critChance = t.stats.critChance;
    s.critMultiplier = t.stats.critMultiplier;
    s.colour = t.archetype.colour;
    s.homing = 1.6;
    s.targetId = target.id;
    s.ownerId = t.id;
    s.chain = r.chain;
    s.chainFalloff = r.chainFalloff;
    s.chainRange = CHAIN_REACH;
    this.copyRiders(r, s);
    this.projectiles.launch();
    void world;
  }

  /** Hitscan, possibly split across several targets. Laser. */
  private fireBeam(world: World, t: Tower, i: number, b: TowerBehaviour, r: Resolved, target: Enemy, damage: number, dt: number): void {
    // Ramp: reward staying on one target, punish switching.
    const perSec = r.rampPerSec;
    const cap = r.rampMax;
    if (perSec > 0) {
      if (this.rampTarget[i] === target.id) this.ramp[i] = Math.min(cap, this.ramp[i] + perSec / Math.max(1, t.stats.fireRate));
      else { this.ramp[i] = 0; this.rampTarget[i] = target.id; }
    }
    const rampMul = 1 + this.ramp[i];

    const beams = r.split;
    this.splitCount = 0;
    this.splitWant = beams;
    if (beams > this.splitIdx.length) this.splitIdx = new Int32Array(beams * 2);
    this.index.forEachInRadius(t.pos.x, t.pos.y, t.stats.range, this.onSplit);

    const shieldMelt = r.shieldMelt;
    const count = Math.max(1, this.splitCount);
    for (let k = 0; k < count; k++) {
      const e = k < this.splitCount ? this.index.list[this.splitIdx[k]] : target;
      if (!e || !e.alive) continue;
      const dx = e.pos.x - t.pos.x, dy = e.pos.y - t.pos.y;
      const d = Math.hypot(dx, dy) || 1;
      const ux = dx / d, uy = dy / d;
      const reach = this.projectiles.hitscan(
        world, t.pos.x + ux * b.muzzle, t.pos.y + uy * b.muzzle, ux, uy,
        Math.min(t.stats.range, d + 24),
        damage * rampMul, t.stats.damageType, Math.max(1, t.stats.pierce),
        t.stats.armourPierce, shieldMelt, t.stats.critChance, t.stats.critMultiplier,
        t.archetype.colour, t, 3,
      );
      this.beamFx(world, t, b, ux, uy, reach);
    }
    void dt;
  }

  /** Copies every status rider a tower's grants imply onto a shot spec. */
  private copyRiders(r: Resolved, s: ReturnType<ProjectileSystem['begin']>): void {
    s.slowFactor = r.slowFactor;
    s.slowSecs = r.slowSecs;
    s.stunSecs = r.stunSecs;
    s.stunChance = r.stunChance;
    s.shatterMul = r.shatter;
    s.shieldMelt = r.shieldMelt;
    s.dotDps = r.dotDps;
    s.dotSecs = r.dotSecs;
    s.dotStacks = r.dotStacks;
    s.spreadRadius = r.spreadRadius;
    s.spreadFraction = r.spreadFraction;
  }

  /** Points a spec at where the target will be, not where it is. */
  private aimLead(s: ReturnType<ProjectileSystem['begin']>, mx: number, my: number, target: Enemy, speed: number): void {
    const dx = target.pos.x - mx, dy = target.pos.y - my;
    const dist = Math.hypot(dx, dy);
    const lead = dist / Math.max(1, speed);
    const px = dx + target.vel.x * lead;
    const py = dy + target.vel.y * lead;
    const d = Math.hypot(px, py) || 1;
    s.dirX = px / d; s.dirY = py / d;
  }

  // ───────────────────────────────────────────────────────────────────── fx ──

  private muzzleFx(world: World, t: Tower, b: TowerBehaviour): void {
    if (b.muzzle <= 0) return;
    const mx = t.pos.x + Math.cos(t.angle) * b.muzzle;
    const my = t.pos.y + Math.sin(t.angle) * b.muzzle;
    this.projectiles.fx.particle(world, mx, my,
      Math.cos(t.angle) * 30, Math.sin(t.angle) * 30,
      0.08, 3, t.archetype.colour, 'muzzle', 3.2);
    for (let k = 0; k < 3; k++) {
      const a = t.angle + (world.rng() - 0.5) * 0.7;
      const sp = 60 + world.rng() * 90;
      this.projectiles.fx.particle(world, mx, my, Math.cos(a) * sp, Math.sin(a) * sp,
        0.1 + world.rng() * 0.08, 1, 'white', 'spark', 2.4, 6);
    }
  }

  private beamFx(world: World, t: Tower, b: TowerBehaviour, ux: number, uy: number, len: number): void {
    const steps = Math.max(2, (len / 5) | 0);
    for (let k = 0; k <= steps; k++) {
      const f = k / steps;
      this.projectiles.fx.particle(world,
        t.pos.x + ux * (b.muzzle + (len - b.muzzle) * f),
        t.pos.y + uy * (b.muzzle + (len - b.muzzle) * f),
        0, 0, 0.06, 1, t.archetype.colour, 'trailDot', 3.4);
    }
  }

  // ────────────────────────────────────────────────────────────── abilities ──

  /** Ability slots tracked per tower. Four is more than any archetype grants. */
  static readonly ABILITY_SLOTS = 4;

  private tickAbilities(i: number, dt: number): void {
    const base = i * TowerSystem.ABILITY_SLOTS;
    for (let k = 0; k < TowerSystem.ABILITY_SLOTS; k++) {
      if (this.abilityCd[base + k] > 0) this.abilityCd[base + k] -= dt;
    }
  }

  /** Remaining cooldown for the `slot`-th ability a tower grants, in seconds. */
  abilityCooldown(towerIndex: number, slot: number): number {
    const v = this.abilityCd[towerIndex * TowerSystem.ABILITY_SLOTS + slot];
    return v > 0 ? v : 0;
  }

  /**
   * Fires a granted ability. Returns false when it is still cooling down or the
   * tower never granted it. The effect itself is deliberately generic — a
   * screen-clearing pulse scaled by the tower's own damage — because the
   * activated-ability catalogue is a balance question, not a plumbing one.
   */
  triggerAbility(world: World, towerIndex: number, name: string): boolean {
    const t = world.towers[towerIndex];
    if (!t) return false;
    let slot = -1, cooldown = 0, seen = 0;
    for (const g of t.grants) {
      if (!g.startsWith('ability:')) continue;
      const parts = g.split(':');
      if (parts[1] === name) { slot = seen; cooldown = parseFloat(parts[2]) || 20; break; }
      seen++;
    }
    if (slot < 0) return false;
    const idx = towerIndex * TowerSystem.ABILITY_SLOTS + Math.min(slot, TowerSystem.ABILITY_SLOTS - 1);
    if (this.abilityCd[idx] > 0) return false;
    this.abilityCd[idx] = cooldown;

    const radius = t.stats.range;
    this.projectiles.fx.shockwave(world, t.pos.x, t.pos.y, 3.4, radius * 1.4, 0.25);
    this.projectiles.fx.particle(world, t.pos.x, t.pos.y, 0, 0, 0.4, radius, 'white', 'ring', 4.0);
    world.fx.shake += 5;

    const s = this.projectiles.begin();
    s.kind = 'arc';
    s.x = t.pos.x; s.y = t.pos.y;
    s.tx = t.pos.x; s.ty = t.pos.y;
    s.flight = 0.01; s.ttl = 0.02;
    s.dirX = 0; s.dirY = 0; s.speed = 0;
    s.damage = t.stats.damage * 12;
    s.damageType = t.stats.damageType;
    s.splash = radius;
    s.splashFalloff = 0.8;
    s.armourPierce = t.stats.armourPierce;
    s.colour = t.archetype.colour;
    s.ownerId = t.id;
    this.copyRiders(this.resolveFor(towerIndex, t), s);
    // The ability overrides whatever stun the tower normally carries.
    s.stunSecs = 1.2; s.stunChance = 1;
    this.projectiles.launch();
    return true;
  }

  // ──────────────────────────────────────────────────────────────── capacity ──

  private ensureCapacity(n: number): void {
    if (n <= this.auraDamage.length) return;
    const cap = 1 << Math.ceil(Math.log2(Math.max(8, n)));
    this.auraDamage = new Float32Array(cap);
    this.auraRate = new Float32Array(cap);
    this.auraCash = new Float32Array(cap);
    const ramp = new Float32Array(cap); ramp.set(this.ramp); this.ramp = ramp;
    const rt = new Int32Array(cap); rt.set(this.rampTarget); this.rampTarget = rt;
    const ab = new Float32Array(cap * TowerSystem.ABILITY_SLOTS);
    ab.set(this.abilityCd); this.abilityCd = ab;
  }
}

/** Convenience for the UI: rebuild derived state after an external tier edit. */
export function refreshTower(t: Tower): void {
  t.stats = applyUpgrades(t.archetype, t.tiers);
  collectGrants(t.archetype, t.tiers, t.grants);
  t.invested = totalInvested(t.archetype, t.tiers);
}

/** Cycles a tower's targeting mode. Exported so the HUD does not duplicate it. */
export function setTargeting(t: Tower, mode: TargetingMode): void {
  t.targeting = mode;
}
